/**
 * Money — convert and format integer cents.
 *
 * Per the data-layer handoff (§2 + §10): every monetary field on the wire is
 * an INTEGER number of cents. The frontend keeps cents internally and only
 * converts to dollars at the final display step. This module centralizes
 * that conversion so no leaf component does ad-hoc math like
 * `value / 100`.
 *
 * The single source of truth for "this number is cents" is *type-level*:
 * downstream code passes numbers around freely, but anything routed through
 * `format()` is expected to be cents. Mistakes here are silent so we lean on
 * naming (`amount_minor_units`, `patient_balance`, etc.) to keep callers
 * honest.
 */

const USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Convert integer cents to dollars (number). */
export function centsToDollars(cents: number): number {
  return cents / 100;
}

/** Convert dollars to integer cents, rounding half-to-even. Use for user input. */
export function dollarsToCents(dollars: number): number {
  return Math.round(dollars * 100);
}

/** Format integer cents as a USD currency string. */
export function format(cents: number): string {
  return USD.format(centsToDollars(cents));
}

/**
 * Sum a list of cent values. Trivial wrapper but used in three or four
 * places — extracted so it reads as a single intent at call sites.
 */
export function sumCents(values: number[]): number {
  return values.reduce((acc, v) => acc + v, 0);
}
